// metro-checkout.component.ts

import { Component, OnInit, AfterViewInit, OnDestroy, Inject, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CreateOrderWithItemsDto, MercadoPagoOrderService, OrderService, StockStatus } from '@proxy/orders';
import { SelectedSommelierService } from '../../services/selected-sommelier.service';
import { MercadoPagoRequestDto, InstallmentOptionDto } from '@proxy/payments/dto';
import { SelectOption } from '../metro-form-select/metro-form-select.component';
import { PickUpDto, PickUpService } from '@proxy/pick-ups';
import { NotificationService } from '../../services/notification.service';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { environment } from 'src/environments/environment';
import { ConfirmationModalService } from '../../services/confirmation-modal.service';
import { ShoppingCartService } from '@proxy/shopping-carts';
import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { CartStateService } from '../../services/cart-state.service';
import { firstValueFrom, switchMap, take } from 'rxjs';
import { CustomProfileService } from '@proxy';
import { CustomUpdateProfileDto } from '@proxy/profiles/dto';
import { PaymentSettingsService } from '@proxy/payments/payment-settings.service';
import { PaywayService } from '@proxy/payments';
import { ARGENTINA_PROVINCES } from '../../constants/argentina-provinces';

declare var MercadoPago: any;
declare var Decidir: any;

// Constantes para métodos de entrega (escalable para futuros métodos)
export enum DeliveryMethod {
  MeetWithSommelier = 'meetWithSommelier',
  Pickup = 'pickup'
}

@Component({
  selector: 'app-metro-checkout',
  templateUrl: './metro-checkout.component.html',
  standalone: false,
  styles: [`
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes fadeOut {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(-10px);
      }
    }
    .fade-in-animation {
      animation: fadeIn 0.3s ease-in-out;
    }
    .fade-out-animation {
      animation: fadeOut 0.3s ease-in-out;
    }
  `]
})
export class MetroCheckoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private pendingOrderId?: number;
  cartItems: any[] = [];
  form!: FormGroup;
  sommelierId: number;
  sommelierUserId?: string;
  DeliveryMethod = DeliveryMethod; // Exponer el enum al template

  // MercadoPago
  mp: any;
  cardForm: any;

  // Payway / Decidir
  decidir: any;
  paywayCardForm: any;
  installmentOptions: InstallmentOptionDto[] = [];
  selectedInstallments: number = 3;
  isLoadingInstallments = false;
  paywayCardBrand: string = 'visa';
  paywayCardBin: string = '';

  // Provider
  activeProvider: string = 'MercadoPago'; // 'MercadoPago' | 'Payway' | 'both'
  selectedCardProvider: string = 'MercadoPago'; // 'MercadoPago' | 'Payway'

  selectCardProvider(provider: string) {
    if (this.selectedCardProvider === provider) return;
    this.selectedCardProvider = provider;
    this.initCardFormByProvider();
  }

  get isPaywayActive(): boolean {
    return this.activeProvider.toLowerCase().includes('payway');
  }
  get isMercadoPagoActive(): boolean {
    return this.activeProvider.toLowerCase().includes('mercadopago');
  }
  get isAllariaActive(): boolean {
    return this.activeProvider.toLowerCase().includes('allaria');
  }

  isLoading = false;
  selectedPickUp: string | null = null;
  pickUpsOptions: SelectOption[] = [];
  pickUps: PickUpDto[] = [];
  userId: string;
  taxRate = 0.21;
  savedPickupValue: string | null = null;
  showPickupSelect: boolean = false;
  userProfile: CustomUpdateProfileDto;
  isProfileIncomplete = false;
  isClient = false;
  isSommelier = false;
  isLoadingProfile = true;
  initialPhoneNumber = '';
  initialDocumentNumber = '';
  initialAddress = '';
  initialProvince = '';
  provinceOptions: SelectOption[] = ARGENTINA_PROVINCES.map(p => ({ value: p, label: p }));

  // Coupon state
  appliedCoupon?: any;
  appliedCouponCode: string = '';
  appliedCouponDisplay: string = '';
  discountAmount: number = 0;

  // Installment plans from MP SDK (onInstallmentsReceived callback)
  private payerCosts: any[] = [];

  // Total mostrado en el resumen (se actualiza al cambiar cuotas)
  displayTotal: number = 0;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private ordersService: OrderService,
    private selectedSommelierService: SelectedSommelierService,
    private pickUpService: PickUpService,
    private mercadoPagoOrderService: MercadoPagoOrderService,
    private notificationService: NotificationService,
    private confService: ConfirmationModalService,
    private shoppingCartService: ShoppingCartService,
    private config: ConfigStateService,
    private localization: LocalizationService,
    private cartStateService: CartStateService,
    private profileService: CustomProfileService,
    private paymentSettingsService: PaymentSettingsService,
    private paywayService: PaywayService
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    const nav = history.state;
    if (nav && nav.items) {
      this.cartItems = nav.items;

      // Recuperar cupón si viene del carrito
      if (nav.appliedCouponCode) {
        this.appliedCouponCode = nav.appliedCouponCode;
        this.appliedCouponDisplay = nav.appliedCouponDisplay || nav.appliedCouponCode;
        this.discountAmount = nav.discountAmount || 0;
      }
    } else {
      this.router.navigate(['/cart']);
    }

    this.loadProfile();

    this.config.getOne$('currentUser').subscribe(res => {
      this.userId = res?.id;
      this.isClient = (res as any)?.roles?.includes('client') || false;
      this.isSommelier = (res as any)?.roles?.includes('sommelier') || false;
      this.updateValidators();
    });

    let slug = this.selectedSommelierService.currentSlugValue;
    this.ordersService.getSommelierBySlugBySlug(slug).subscribe(res => {
      this.sommelierId = res.id;
      this.sommelierUserId = res.userId;
    });

    this.form.get('paymentMethod').valueChanges.subscribe(value => {
      if (value === 'card') {
        setTimeout(() => this.initCardFormByProvider(), 0);
      } else {
        this.destroyCardForm();
        this.destroyPaywayForm();
      }
    });
    this.loadPikUps();

    // Detectar proveedor activo y configurar el formulario correspondiente
    this.paymentSettingsService.getActiveProvider().subscribe({
      next: (result: any) => {
        this.activeProvider = result?.provider ?? 'MercadoPago';
        console.log('[Checkout] Proveedor activo:', this.activeProvider);
        // Re-iniciar el formulario de tarjeta según proveedor
        if (this.form.get('paymentMethod')?.value === 'card') {
          setTimeout(() => this.initCardFormByProvider(), 100);
        }
      },
      error: (err) => {
        console.error('Error al obtener el proveedor de pago activo:', err);
      }
    });
  }

  onCouponApplied(result: any) {
    this.appliedCoupon = result;
    this.appliedCouponCode = result.coupon?.code || '';
    this.appliedCouponDisplay = result.coupon?.discountType === 0
      ? `${result.coupon.discountValue}%`
      : (result.coupon?.code || '');
    this.discountAmount = result.discountAmount;

    // Si MP está inicializado, actualizar el monto
    if (this.cardForm) {
      this.initCardForm();
    }
    // Payway: recalcular las cuotas contra el nuevo total con descuento. Si no, se paga el
    // monto pre-cupón y el descuento viaja disfrazado de "interés" (winepassport-api#59).
    if (this.paywayCardForm) {
      this.loadInstallments();
    }
  }

  onCouponRemoved() {
    this.appliedCoupon = undefined;
    this.appliedCouponCode = '';
    this.appliedCouponDisplay = '';
    this.discountAmount = 0;

    // Si MP está inicializado, actualizar el monto
    if (this.cardForm) {
      this.initCardForm();
    }
    // Payway: recalcular las cuotas contra el total sin descuento (winepassport-api#59).
    if (this.paywayCardForm) {
      this.loadInstallments();
    }
  }

  ngAfterViewInit(): void {
    // El form se inicializa una vez que el proveedor activo sea conocido
    // (el subscribe en ngOnInit lo maneja con el setTimeout)
  }

  /** Inicializa el form de tarjeta del proveedor correcto */
  private initCardFormByProvider() {
    const usePayway = (this.isPaywayActive && !this.isMercadoPagoActive) || 
                      (this.isPaywayActive && this.isMercadoPagoActive && this.selectedCardProvider === 'Payway');
    if (usePayway) {
      this.destroyCardForm();
      this.initPaywayForm();
    } else {
      this.destroyPaywayForm();
      this.initCardForm();
    }
  }

  /** Inicializa el SDK de Decidir (Payway) y carga cuotas */
  private initPaywayForm() {
    // Cargar SDK de Decidir si no está
    this.loadDecidirSdk().then(() => {
      try {
        const publicKey = environment['payway']?.publicKey || '9ZiAulIeUZ7xvR9NeuoECA3eT0nJhy3e';
        const isPaywayProduction = environment['payway']?.env === 'production';
        const envUrl = isPaywayProduction
          ? 'https://ventasonline.payway.com.ar/api/v2'
          : 'https://developers-ventasonline.payway.com.ar/api/v2';

        this.decidir = new Decidir(envUrl, true);
        this.decidir.setPublishableKey(publicKey);
        this.decidir.setTimeout(5000);
        
        this.paywayCardForm = {}; // marca como inicializado
        this.loadInstallments();
        console.log('[Payway] SDK Decidir inicializado con URL:', envUrl);
      } catch (e) {
        console.error('[Payway] Error al inicializar SDK Decidir:', e);
      }
    });
  }

  private loadDecidirSdk(): Promise<void> {
    return new Promise((resolve) => {
      if (typeof Decidir !== 'undefined') { resolve(); return; }
      const script = document.createElement('script');
      script.src = 'https://ventasonline.payway.com.ar/static/v2.6.4/decidir.js';
      script.onload = () => resolve();
      script.onerror = () => {
        console.warn('[Payway] No se pudo cargar decidir.js');
        resolve();
      };
      document.head.appendChild(script);
    });
  }

  loadInstallments() {
    if (!this.totalWithTax) return;
    this.isLoadingInstallments = true;
    this.paywayService.calculateInstallments(this.totalWithTax, this.paywayCardBrand, this.paywayCardBin).subscribe({
      next: (opts) => {
        this.installmentOptions = opts;
        // Preseleccionar siempre 1 cuota (pago único); si es débito, es la única opción disponible
        this.selectedInstallments = 1;
        this.isLoadingInstallments = false;
        console.log('[Payway] Cuotas cargadas:', opts);
      },
      error: (err) => {
        console.error('[Payway] Error al cargar cuotas:', err);
        this.isLoadingInstallments = false;
      }
    });
  }

  get selectedInstallmentOption(): InstallmentOptionDto | undefined {
    return this.installmentOptions.find(o => o.quotas === this.selectedInstallments);
  }

  get totalWithInstallments(): number {
    return this.selectedInstallmentOption?.total ?? this.totalWithTax;
  }

  get monthlyPayment(): number {
    return this.selectedInstallmentOption?.monthlyPayment ?? this.totalWithTax;
  }

  /** Genera el token de Payway/Decidir y envía la orden */
  async submitPayway() {
    if (this.form.get('deliveryMethod')?.value === DeliveryMethod.Pickup) {
      this.form.get('shippingAddressLine')?.markAsTouched();
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isLoading = true;
    try {
      await this.syncProfileIfNeeded();
    } catch {
    this.isLoading = false;
      return;
    }

    try {
      // Recopilar datos del formulario Payway
      const cardNumber = (document.getElementById('payway-card-number') as HTMLInputElement)?.value?.replace(/\s/g, '');
      // Recalcular marca y BIN por las dudas (ej. autocompletado del navegador, que no dispara el evento input)
      this.paywayCardBrand = this.detectCardBrand(cardNumber);
      this.paywayCardBin = (cardNumber || '').replace(/\D/g, '').substring(0, 6);
      const expirationDate = ((document.getElementById('payway-expiration-date') as HTMLInputElement)?.value || '').replace(/\s/g, '');
      
      let cardExpMonth = '';
      let cardExpYear = '';

      if (expirationDate.includes('/')) {
        const dateParts = expirationDate.split('/');
        cardExpMonth = dateParts[0]?.trim() || '';
        cardExpYear = dateParts[1]?.trim() || '';
      } else if (expirationDate.length === 4) {
        cardExpMonth = expirationDate.substring(0, 2);
        cardExpYear = expirationDate.substring(2, 4);
      } else if (expirationDate.length === 6) {
        cardExpMonth = expirationDate.substring(0, 2);
        cardExpYear = expirationDate.substring(2, 6);
      }

      if (cardExpMonth.length === 1) {
        cardExpMonth = '0' + cardExpMonth;
      }
      if (cardExpYear.length === 2) {
        cardExpYear = '20' + cardExpYear;
      }

      // Poblar los campos ocultos que el SDK Decidir v2 lee via data-decidir
      const expMonthEl = document.getElementById('payway-exp-month') as HTMLInputElement;
      const expYearEl = document.getElementById('payway-exp-year') as HTMLInputElement;
      if (expMonthEl) expMonthEl.value = cardExpMonth;
      if (expYearEl) expYearEl.value = cardExpYear;

      const docTypeEl = document.getElementById('payway-doc-type') as HTMLInputElement;
      const docNumberEl = document.getElementById('payway-doc-number') as HTMLInputElement;
      if (docTypeEl) docTypeEl.value = 'dni';
      if (docNumberEl) docNumberEl.value = this.form.get('dni')?.value || '';

      // Tokenizar usando el método correcto del SDK Decidir v2: createToken(form, callback)
      if (!this.decidir) {
        throw new Error('SDK Decidir no inicializado. Recargá la página e intentá de nuevo.');
      }

      const formEl = document.getElementById('form-payway') as HTMLFormElement;
      if (!formEl) {
        throw new Error('Formulario Payway no encontrado.');
      }

      const publicKey = environment['payway']?.publicKey || '9ZiAulIeUZ7xvR9NeuoECA3eT0nJhy3e';
      console.log('[Payway] Public key usada:', publicKey);
      console.log('[Payway] decidir instance:', this.decidir);
      const yearInput = document.getElementById('payway-exp-year') as HTMLInputElement;
      const monthInput = document.getElementById('payway-exp-month') as HTMLInputElement;

      if (yearInput && yearInput.value.length === 4) {
        yearInput.value = yearInput.value.slice(-2);
      }
      if (monthInput && monthInput.value.length === 1) {
        monthInput.value = monthInput.value.padStart(2, '0');
      }

      console.log('[Payway Debug] HTML del formulario POST-SLICE:', formEl.innerHTML);
      console.log('[Payway Debug] Valor card_expiration_month POST-SLICE:', (formEl.querySelector('[data-decidir=card_expiration_month]') as HTMLInputElement)?.value);
      console.log('[Payway Debug] Valor card_expiration_year POST-SLICE:', (formEl.querySelector('[data-decidir=card_expiration_year]') as HTMLInputElement)?.value);

      console.log('[Payway] Solicitando token al SDK Decidir con createToken...');
      const tokenData = await new Promise<any>((resolve, reject) => {
        this.decidir.createToken(formEl, (status: number, response: any) => {
          console.log('[Payway] status:', status);
          console.log('[Payway] response completo:', JSON.stringify(response));
          if (status === 200 || status === 201) {
            resolve(response);
          } else {
            reject(response);
          }
        });
      });

      const token: string = tokenData.id;
      console.log('[Payway] Token generado correctamente:', token);

      const orderDto = this.buildOrderDto();
      orderDto.paymentMethod = 'payway';
      orderDto.paywayToken = token;
      orderDto.paywayInstallments = this.selectedInstallments;

      const combinedDto = {
        pago: {
          token: token,
          paymentMethodId: this.paywayCardBrand,
          installments: this.selectedInstallments,
          amount: this.totalWithInstallments,
          interestCharge: this.totalWithInstallments - this.totalWithTax,
          issuerId: '0',
          payer: { 
            email: (document.getElementById('payway-email') as HTMLInputElement)?.value,
            identification: {
              type: 'DNI',
              number: this.form.get('dni')?.value || ''
            }
          }
        },
        order: { ...orderDto, existingOrderId: this.pendingOrderId }
      };

      this.mercadoPagoOrderService.confirmPaymentAndCreateOrderByDto(combinedDto).subscribe({
        next: (resp) => this.handlePaymentResponse(resp, combinedDto),
        error: (err) => {
          console.error('[Payway] Error al procesar pago:', err);
          this.notificationService.error('', 'Error al procesar el pago con Payway');
          this.isLoading = false;
        }
      });
    } catch (err) {
      console.error('[Payway] Error al tokenizar tarjeta:', err);
      this.notificationService.error('', 'Error al procesar los datos de la tarjeta. Verificá los datos ingresados.');
      this.isLoading = false;
    }
  }

  /** shippingAddressLine es en realidad el ColppyWarehouseName del punto de retiro (para el
   * remito/validación de stock), no una dirección — buscamos la dirección real del PickUp
   * correspondiente para mandarla aparte (issue #330). */
  private getPickupAddress(colppyWarehouseName: string | undefined): string | undefined {
    if (!colppyWarehouseName) return undefined;
    return this.pickUps.find(p => p.colppyWarehouseName === colppyWarehouseName)?.address;
  }

  private buildOrderDto(): CreateOrderWithItemsDto {
    const formValue = this.form.value;
    const deliveryMethod = formValue.deliveryMethod;
    const deliveryMethodText = deliveryMethod === DeliveryMethod.MeetWithSommelier ? 'Meet with Sommelier' : 'Pickup';
    let notes = formValue.notes || '';
    if (notes) notes += ' | ';
    notes += `Delivery Method: ${deliveryMethodText}`;
    let shippingAddress = deliveryMethodText;
    if (deliveryMethod === DeliveryMethod.Pickup) {
      shippingAddress = formValue.shippingAddressLine || (this.pickUpsOptions[0]?.value ?? deliveryMethodText);
    } else {
      shippingAddress = this.pickUpsOptions[0]?.value ?? deliveryMethodText;
    }
    return {
      sommelierId: this.sommelierId,
      shippingAddressLine: shippingAddress,
      pickupAddress: this.getPickupAddress(shippingAddress),
      notes,
      paymentMethod: formValue.paymentMethod,
      deliveryMethod,
      orderItems: this.cartItems.map(i => ({
        wineId: i.wineId, quantity: i.quantity, unitPrice: i.unitPrice,
        externalCode: i.wineExternalCode, wineName: i.wineName
      })),
      couponCode: this.appliedCouponCode
    };
  }

  private handlePaymentResponse(paymentResponse: any, combinedDto: any) {
    this.isLoading = false;

    if (paymentResponse.stockIssues?.length) {
      const winesList = paymentResponse.stockIssues
        .map((i: any) => {
          const availableQty = Math.floor(i.availableQuantity);
          if (i.status === StockStatus.Insufficient) {
            return `• ${i.wineName} (Stock disponible: ${availableQty} unidades)`;
          }
          return `• ${i.wineName}`;
        })
        .join('<br>');

      const deliveryMethod = this.form.get('deliveryMethod')?.value;
      const isPickup = deliveryMethod === DeliveryMethod.Pickup;
      // Si es pickup pero no hay stock en NINGUN punto de retiro, tratar igual que MeetWithSommelier (sin opción de continuar)
      const canForce = isPickup && !paymentResponse.noStockInAnyDeposit;

      if (canForce) {
        const body =
          `${this.localization.instant('::StockModalUnavailableList')}<br>${winesList}<br><br>` +
          `${this.localization.instant('::StockModalInstructions')}`;

        this.confService.confirm({
          title: this.localization.instant('::StockWarningTitle'),
          message: body,
          type: 'warning',
          showCancelButton: true,
          confirmText: this.localization.instant('::StockModalContinue'),
          cancelText: this.localization.instant('::StockModalCancel'),
        }).subscribe(wantsToContinue => {
          if (wantsToContinue) {
            // Reenviar con forceCreate: true
            const forceDto = { ...combinedDto, order: { ...combinedDto.order, forceCreate: true } };
            this.isLoading = true;
            this.mercadoPagoOrderService.confirmPaymentAndCreateOrderByDto(forceDto).subscribe({
              next: (finalResponse) => {
                this.isLoading = false;
                if (finalResponse.paymentStatus === 'approved') {
                  this.cartStateService.clear();
                  sessionStorage.removeItem('appliedCoupon');
                  this.selectedSommelierService.navigate(['payment-status'], {
                    queryParams: {
                      status: 'success',
                      orderId: finalResponse.orderId,
                      paymentTransactionId: finalResponse.paymentTransactionId,
                      redirectAfter: 5000
                    }
                  });
                } else {
                  this.pendingOrderId = finalResponse.orderId;
                  const title = this.localization.instant('::PaymentRejectedTitle') || 'Pago rechazado';
                  const errorMsg = finalResponse.message || this.localization.instant('::PaymentRejectedBody');
                  this.confService.confirm({ title, message: errorMsg, type: 'danger', confirmText: this.localization.instant('::Accept'), showCancelButton: false }).subscribe();
                  this.destroyAndReset();
                }
              },
              error: (err) => {
                this.isLoading = false;
                this.notificationService.error('', 'Error al procesar el pago');
                this.destroyAndReset();
              }
            });
          }
        });
      } else {
        // No hay stock en ningún punto (o método != pickup): no permitimos continuar
        const body =
          `${this.localization.instant('::StockWarningMessageAllDeposits')}<br>${winesList}<br><br>` +
          `${this.localization.instant('::StockWarningInstructionsAllDeposits')}`;

        this.confService.confirm({
          title: this.localization.instant('::StockWarningTitleAllDeposits'),
          message: body,
          type: 'warning',
          showCancelButton: true,
          confirmText: this.localization.instant('::StockModalCancel'),
          cancelText: this.localization.instant('::StockModalBackToCart'),
        }).subscribe(wantsToCancel => {
          if (!wantsToCancel) {
            // Volver al carrito
            this.selectedSommelierService.navigate(['shopping-cart']);
          }
        });
      }
      return;
    }

    if (paymentResponse.paymentStatus === 'approved') {
      this.cartStateService.clear();
      sessionStorage.removeItem('appliedCoupon');
      this.selectedSommelierService.navigate(['payment-status'], {
        queryParams: {
          status: 'success',
          orderId: paymentResponse.orderId,
          paymentTransactionId: paymentResponse.paymentTransactionId,
          redirectAfter: 5000
        }
      });
    } else if (paymentResponse.paymentStatus === 'fraud') {
      this.confService.confirm({
        title: '⚠️ Inconsistencia de precios',
        message: 'Detectamos una inconsistencia entre el precio enviado y nuestros registros. ' +
          'Por favor, volvé al carrito y realizá el pedido nuevamente.',
        type: 'warning',
        confirmText: 'Volver al carrito',
        showCancelButton: false,
      }).subscribe(() => {
        this.selectedSommelierService.navigate(['shopping-cart']);
      });
      this.destroyAndReset();
    } else {
      this.pendingOrderId = paymentResponse.orderId;
      const title = this.localization.instant('::PaymentRejectedTitle') || 'Pago rechazado';
      const errorMsg = paymentResponse.message || this.localization.instant('::PaymentRejectedBody') || 'Tu pago fue rechazado. Revisá tu medio de pago e intentá nuevamente.';
      this.confService.confirm({
        title: title,
        message: errorMsg,
        type: 'danger',
        confirmText: this.localization.instant('::Accept') || 'Aceptar',
        showCancelButton: false,
      }).subscribe();
      this.destroyAndReset();
    }
  }

  private destroyPaywayForm() {
    this.paywayCardForm = null;
    this.installmentOptions = [];
  }

  protected buildForm() {
    this.form = this.fb.group({
      deliveryMethod: [DeliveryMethod.MeetWithSommelier, Validators.required],
      shippingAddressLine: [''],
      notes: [''],
      paymentMethod: ['card', Validators.required],
      bankAlias: [{ value: 'Alias - WINE.P', disabled: true }],
      bankCbu: [{ value: 'CBU - 0170093020000033322542', disabled: true }],
      bankHolder: [{ value: 'Titular - GLOBEXA S.A.', disabled: true }],
      dni: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      profileAddress: ['', Validators.required],
      profileProvince: ['', Validators.required],
    });

    // Inicializar showPickupSelect según el valor inicial
    this.showPickupSelect = this.form.get('deliveryMethod')?.value === DeliveryMethod.Pickup;

    // Hacer que shippingAddressLine sea requerido solo cuando se selecciona Pickup
    this.form.get('deliveryMethod')?.valueChanges.subscribe(method => {
      const shippingControl = this.form.get('shippingAddressLine');
      if (method === DeliveryMethod.Pickup) {
        // Mostrar el select con animación
        this.showPickupSelect = true;
        shippingControl?.setValidators([Validators.required]);
        // Restaurar el valor guardado si existe, o mantener el actual si ya tiene uno
        if (this.savedPickupValue && !shippingControl?.value) {
          shippingControl?.setValue(this.savedPickupValue);
        }
      } else {
        // Ocultar el select con animación de salida
        // Esperar a que termine la animación antes de eliminar del DOM
        setTimeout(() => {
          this.showPickupSelect = false;
        }, 300); // Duración de la animación (300ms)

        // Guardar el valor actual antes de limpiar (solo si tiene un valor)
        const currentValue = shippingControl?.value;
        if (currentValue) {
          this.savedPickupValue = currentValue;
        }
        shippingControl?.clearValidators();
        // NO limpiar el valor, solo quitar la validación requerida
        // shippingControl?.setValue(''); // Comentado para mantener la selección
      }
      shippingControl?.updateValueAndValidity();
    });
  }

  private initCardForm() {
    if (this.cardForm) {
      this.cardForm.unmount();
    }

    this.mp = new MercadoPago(environment.mercadoPago.publicKey, { locale: 'es-AR' });

    this.cardForm = this.mp.cardForm({
      amount: this.totalWithTax.toFixed(2),
      autoMount: true,
      form: {
        id: 'form-checkout',
        cardNumber: { id: 'form-checkout__cardNumber' },
        expirationDate: { id: 'form-checkout__expirationDate' },
        securityCode: { id: 'form-checkout__securityCode' },
        cardholderName: { id: 'form-checkout__cardholderName' },
        cardholderEmail: { id: 'form-checkout__cardholderEmail' },
        identificationType: { id: 'form-checkout__identificationType' },
        identificationNumber: { id: 'form-checkout__identificationNumber' },
        issuer: { id: 'form-checkout__issuer' },
        installments: { id: 'form-checkout__installments' } // Mantener para validación, pero oculto con CSS
      },
      callbacks: {
        onFormMounted: error => {
          if (error) return console.warn('❌ Error al montar form-checkout:', error);
          // Escuchar cambios en el select de cuotas para actualizar el Total visible
          this.displayTotal = this.totalWithTax;
          setTimeout(() => {
            const installmentSelect = document.getElementById('form-checkout__installments');
            if (installmentSelect) {
              installmentSelect.addEventListener('change', () => this.onInstallmentChanged());
            }
          }, 500); // Esperar a que MP termine de renderizar
        },
        onInstallmentsReceived: (error: any, data: any) => {
          if (error) {
            console.warn('Error al obtener cuotas:', error);
            return;
          }
          // data.payer_costs contiene los planes de cuotas con total_amount (total con intereses)
          this.payerCosts = data?.payer_costs || [];
        },
        onSubmit: async event => {
          event.preventDefault();
          if (this.isLoading) {
            return;
          }
          // Solo validar shippingAddressLine si el método de entrega es Pickup
          if (this.form.get('deliveryMethod')?.value === DeliveryMethod.Pickup) {
            this.form.get('shippingAddressLine')?.markAsTouched();
          }
          if (this.form.invalid) {
            console.error('El formulario tiene errores. Por favor, completa todos los campos requeridos.');
            return;
          }

          this.isLoading = true;

          try {
            await this.syncProfileIfNeeded();
          } catch (error) {
            this.isLoading = false;
            return;
          }

          const tokenData = await this.cardForm.getCardFormData();

          // Asegurar que installments siempre tenga un valor válido (1 cuota por defecto)
          let installmentsValue = tokenData.installments
            ? parseInt(tokenData.installments, 10)
            : 1;

          if (isNaN(installmentsValue) || installmentsValue < 1) {
            installmentsValue = 1;
          }

          // Calcular el cargo por intereses de financiación (cuotas con interés)
          // Se enviará como metadata/referencia al backend para servir como fallback
          let interestCharge = 0;
          let paymentAmount = parseFloat(tokenData.amount); // monto a cobrar (sin interés por defecto)

          if (installmentsValue > 1 && this.payerCosts.length > 0) {
            const plan = this.payerCosts.find((p: any) => parseInt(p.installments, 10) === installmentsValue);
            if (plan && plan.total_amount > this.totalWithTax) {
              interestCharge = plan.total_amount - this.totalWithTax;
            }
          }

          const mpDto: MercadoPagoRequestDto = {
            token: tokenData.token,
            paymentMethodId: tokenData.paymentMethodId,
            issuerId: tokenData.issuerId,
            installments: installmentsValue,
            amount: paymentAmount,
            interestCharge: interestCharge,
            payer: {
              email: tokenData.cardholderEmail,
              identification: {
                type: tokenData.identificationType,
                number: tokenData.identificationNumber
              }
            }
          }

          const formValue = this.form.value;
          const deliveryMethod = formValue.deliveryMethod;
          const deliveryMethodText = deliveryMethod === DeliveryMethod.MeetWithSommelier
            ? 'Meet with Sommelier'
            : 'Pickup';

          // Construir las notas incluyendo el método de entrega
          let notes = formValue.notes || '';
          if (notes) notes += ' | ';
          notes += `Delivery Method: ${deliveryMethodText}`;

          // Si es Pickup, usar el valor seleccionado o el primer valor de pickUpsOptions
          // Si es MeetWithSommelier, también usar el primer valor de pickUpsOptions
          let shippingAddress = deliveryMethodText;
          if (deliveryMethod === DeliveryMethod.Pickup) {
            shippingAddress = formValue.shippingAddressLine || (this.pickUpsOptions.length > 0 ? this.pickUpsOptions[0].value : deliveryMethodText);
          } else if (deliveryMethod === DeliveryMethod.MeetWithSommelier) {
            shippingAddress = this.pickUpsOptions.length > 0 ? this.pickUpsOptions[0].value : deliveryMethodText;
          }

          const orderDto: CreateOrderWithItemsDto = {
            sommelierId: this.sommelierId,
            shippingAddressLine: shippingAddress,
            pickupAddress: this.getPickupAddress(shippingAddress),
            notes: notes,
            paymentMethod: formValue.paymentMethod,
            deliveryMethod: deliveryMethod, // Enviar el valor del enum como key
            orderItems: this.cartItems.map(i => ({
              wineId: i.wineId,
              quantity: i.quantity,
              unitPrice: i.unitPrice,
              externalCode: i.wineExternalCode,
              wineName: i.wineName
            })),
            couponCode: this.appliedCouponCode
          };

          const combinedDto = {
            pago: mpDto,
            order: { ...orderDto, existingOrderId: this.pendingOrderId }
          };

          this.mercadoPagoOrderService.confirmPaymentAndCreateOrderByDto(combinedDto).subscribe({
            next: (paymentResponse) => {
              this.isLoading = false;

              if (paymentResponse.stockIssues?.length) {
                const winesList = paymentResponse.stockIssues
                  .map(i => {
                    const availableQty = Math.floor(i.availableQuantity);
                    if (i.status === StockStatus.Insufficient) {
                      return `• ${i.wineName} (Stock disponible: ${availableQty} unidades)`;
                    }
                    return `• ${i.wineName}`;
                  })
                  .join('<br>');

                const deliveryMethod = this.form.get('deliveryMethod')?.value;
                const isPickup = deliveryMethod === DeliveryMethod.Pickup;
                // Si es pickup pero no hay stock en NINGUN punto de retiro, tratar igual que MeetWithSommelier (sin opción de continuar)
                const canForce = isPickup && !paymentResponse.noStockInAnyDeposit;

                if (canForce) {
                  const body =
                    `${this.localization.instant('::StockModalUnavailableList')}<br>${winesList}<br><br>` +
                    `${this.localization.instant('::StockModalInstructions')}`;

                  this.confService.confirm({
                    title: this.localization.instant('::StockWarningTitle'),
                    message: body,
                    type: 'warning',
                    showCancelButton: true,
                    confirmText: this.localization.instant('::StockModalContinue'),
                    cancelText: this.localization.instant('::StockModalBackToCart'),
                  }).subscribe(wantsToContinue => {
                    if (wantsToContinue) {
                      // Reenviar con forceCreate: true
                      const forceDto = { ...combinedDto, order: { ...combinedDto.order, forceCreate: true } };
                      this.isLoading = true;
                      this.mercadoPagoOrderService.confirmPaymentAndCreateOrderByDto(forceDto).subscribe({
                        next: (finalResponse) => {
                          this.isLoading = false;
                          if (finalResponse.paymentStatus === 'approved') {
                            this.cartStateService.clear();
                            sessionStorage.removeItem('appliedCoupon');
                            this.selectedSommelierService.navigate(['payment-status'], {
                              queryParams: {
                                status: 'success',
                                orderId: finalResponse.orderId,
                                paymentTransactionId: finalResponse.paymentTransactionId,
                                redirectAfter: 5000
                              }
                            });
                          } else {
                            this.pendingOrderId = finalResponse.orderId;
                            const title = this.localization.instant('::PaymentRejectedTitle') || 'Pago rechazado';
                            const errorMsg = finalResponse.message || this.localization.instant('::PaymentRejectedBody');
                            this.confService.confirm({ title, message: errorMsg, type: 'danger', confirmText: this.localization.instant('::Accept'), showCancelButton: false }).subscribe();
                            this.destroyAndReset();
                          }
                        },
                        error: (err) => {
                          this.isLoading = false;
                          this.notificationService.error('', 'Error al procesar el pago');
                          this.destroyAndReset();
                        }
                      });
                    } else {
                      // Volver al carrito
                      this.selectedSommelierService.navigate(['shopping-cart']);
                    }
                  });
                } else {
                  // No hay stock en ningún punto (o método != pickup): no permitimos continuar
                  const body =
                    `${this.localization.instant('::StockWarningMessageAllDeposits')}<br>${winesList}<br><br>` +
                    `${this.localization.instant('::StockWarningInstructionsAllDeposits')}`;

                  this.confService.confirm({
                    title: this.localization.instant('::StockWarningTitleAllDeposits'),
                    message: body,
                    type: 'warning',
                    showCancelButton: true,
                    confirmText: this.localization.instant('::StockModalCancel'),
                    cancelText: this.localization.instant('::StockModalBackToCart'),
                  }).subscribe(wantsToCancel => {
                    if (!wantsToCancel) {
                      // Volver al carrito
                      this.selectedSommelierService.navigate(['shopping-cart']);
                    }
                  });
                }
                return;
              }

              if (paymentResponse.paymentStatus === 'approved') {
                this.cartStateService.clear();
                sessionStorage.removeItem('appliedCoupon');

                this.selectedSommelierService.navigate(['payment-status'], {
                  queryParams: {
                    status: 'success',
                    orderId: paymentResponse.orderId,
                    paymentTransactionId: paymentResponse.paymentTransactionId,
                    redirectAfter: 5000
                  }
                });

              } else if (paymentResponse.paymentStatus === 'fraud') {
                this.confService.confirm({
                  title: '⚠️ Inconsistencia de precios',
                  message: 'Detectamos una inconsistencia entre el precio enviado y nuestros registros. ' +
                    'Por favor, volvé al carrito y realizá el pedido nuevamente.',
                  type: 'warning',
                  confirmText: 'Volver al carrito',
                  showCancelButton: false,
                }).subscribe(() => {
                  this.selectedSommelierService.navigate(['shopping-cart']);
                });
                this.destroyAndReset();

              } else if (paymentResponse.paymentStatus === 'rejected' || paymentResponse.paymentStatus === 'cancelled') {
                // Solo reintentable si fue rechazado/cancelado (terminal). in_process puede confirmarse asíncrono.
                this.pendingOrderId = paymentResponse.orderId;
                const title = this.localization.instant('::PaymentRejectedTitle') || 'Pago rechazado';
                const errorMsg = paymentResponse.message || this.localization.instant('::PaymentRejectedBody') || 'Tu pago fue rechazado. Revisá tu medio de pago e intentá nuevamente.';

                this.confService.confirm({
                  title: title,
                  message: errorMsg,
                  type: 'danger',
                  confirmText: this.localization.instant('::Accept') || 'Aceptar',
                  showCancelButton: false,
                }).subscribe();


                this.destroyAndReset();
              }

            },
            error: (err) => {
              console.error('❌ Error al procesar pago', err);
              if (this.isStockError(err)) {
                this.notificationService.warning('::InsufficientStockWarning', '::StockWarning');
              } else {
                this.notificationService.error('', 'Error al procesar el pago');
              }
              this.destroyAndReset();
              this.isLoading = false;
            }
          });
        }
      }
    });
  }

  destroyAndReset() {

    this.destroyCardForm();
    setTimeout(() => this.initCardForm(), 100);

  }


  async submit() {
    if (this.isLoading) {
      return;
    }
    // Solo validar shippingAddressLine si el método de entrega es Pickup
    if (this.form.get('deliveryMethod')?.value === DeliveryMethod.Pickup) {
      this.form.get('shippingAddressLine')?.markAsTouched();
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    try {
      await this.syncProfileIfNeeded();
    } catch (error) {
      this.isLoading = false;
      return;
    }

    const formValue = this.form.value;
    const deliveryMethod = formValue.deliveryMethod;
    const deliveryMethodText = deliveryMethod === DeliveryMethod.MeetWithSommelier
      ? 'Meet with Sommelier'
      : 'Pickup';

    // Construir las notas incluyendo el método de entrega
    let notes = formValue.notes || '';
    if (notes) notes += ' | ';
    notes += `Delivery Method: ${deliveryMethodText}`;

    // Si es Pickup, usar el valor seleccionado o el primer valor de pickUpsOptions
    // Si es MeetWithSommelier, también usar el primer valor de pickUpsOptions
    let shippingAddress = deliveryMethodText;
    if (deliveryMethod === DeliveryMethod.Pickup) {
      shippingAddress = formValue.shippingAddressLine || (this.pickUpsOptions.length > 0 ? this.pickUpsOptions[0].value : deliveryMethodText);
    } else if (deliveryMethod === DeliveryMethod.MeetWithSommelier) {
      shippingAddress = this.pickUpsOptions.length > 0 ? this.pickUpsOptions[0].value : deliveryMethodText;
    }

    const orderDto: CreateOrderWithItemsDto = {
      sommelierId: this.sommelierId,
      shippingAddressLine: shippingAddress,
      pickupAddress: this.getPickupAddress(shippingAddress),
      notes: notes,
      paymentMethod: formValue.paymentMethod,
      deliveryMethod: deliveryMethod, // Enviar el valor del enum como key
      orderItems: this.cartItems.map(i => ({
        wineId: i.wineId,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
        externalCode: i.wineExternalCode,
        wineName: i.wineName
      })),
      couponCode: this.appliedCouponCode
    };

    if (formValue.paymentMethod === 'card') {
      const formEl = document.getElementById('form-checkout') as HTMLFormElement;
      formEl?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } else {
      // Propagar existingOrderId si hay un reintento pendiente (para Allaria y transferencia)
      if (this.pendingOrderId) {
        orderDto.existingOrderId = this.pendingOrderId;
      }
      if (formValue.paymentMethod === 'allaria') {
        await this.payWithAllaria(orderDto);
      } else {
        await this.handleTransfer(orderDto);
      }
    }
  }

  get total(): number {
    return this.cartItems.reduce((s, i) => s + (i.unitPrice * i.quantity), 0);
  }

  get tax(): number {
    return this.total * (this.taxRate / (1 + this.taxRate));
  }

  get subtotal(): number {
    return this.total - this.tax;
  }

  get totalWithTax(): number {
    const total = this.total - this.discountAmount;
    return total > 0 ? total : 0;
  }

  get shouldShowDni(): boolean {
    return (this.isClient || this.isSommelier) && !this.initialDocumentNumber;
  }

  get shouldShowPhone(): boolean {
    return (this.isClient || this.isSommelier) && !this.initialPhoneNumber;
  }

  get shouldShowAddress(): boolean {
    return (this.isClient || this.isSommelier) && (!this.initialAddress || !this.initialProvince);
  }

  get shouldShowPersonalInformation(): boolean {
    return (this.isClient || this.isSommelier) && (this.shouldShowDni || this.shouldShowPhone || this.shouldShowAddress);
  }

  private updateValidators() {
    const dniControl = this.form.get('dni');
    const phoneControl = this.form.get('phoneNumber');
    const addressControl = this.form.get('profileAddress');
    const provinceControl = this.form.get('profileProvince');

    if (this.shouldShowDni) {
      dniControl?.setValidators([Validators.required]);
    } else {
      dniControl?.clearValidators();
    }

    if (this.shouldShowPhone) {
      phoneControl?.setValidators([Validators.required]);
    } else {
      phoneControl?.clearValidators();
    }

    if (this.shouldShowAddress) {
      addressControl?.setValidators([Validators.required]);
      provinceControl?.setValidators([Validators.required]);
    } else {
      addressControl?.clearValidators();
      provinceControl?.clearValidators();
    }

    dniControl?.updateValueAndValidity();
    phoneControl?.updateValueAndValidity();
    addressControl?.updateValueAndValidity();
    provinceControl?.updateValueAndValidity();
  }


  private destroyCardForm() {
    if (this.cardForm) {
      // El SDK de MercadoPago tira "CardForm is not mounted" si el form nunca llegó a
      // montarse (p. ej. se pagó con Payway). Es teardown: un throw acá abortaba la
      // navegación post-pago y la app mostraba el error [404].
      try {
        this.cardForm.unmount();
      } catch {
        /* noop: el CardForm no estaba montado */
      }
      this.cardForm = null;
    }
    this.payerCosts = [];
    this.displayTotal = 0;
  }

  private onInstallmentChanged(): void {
    const select = document.getElementById('form-checkout__installments') as HTMLSelectElement | null;
    if (!select) return;

    const selectedInstallments = parseInt(select.value, 10);
    if (isNaN(selectedInstallments) || selectedInstallments <= 1 || this.payerCosts.length === 0) {
      this.displayTotal = this.totalWithTax;
      return;
    }

    const plan = this.payerCosts.find((p: any) => parseInt(p.installments, 10) === selectedInstallments);
    if (plan && plan.total_amount > this.totalWithTax) {
      this.displayTotal = plan.total_amount;
    } else {
      this.displayTotal = this.totalWithTax;
    }
  }

  ngOnDestroy(): void {
    this.destroyCardForm();
    this.destroyPaywayForm();
  }

  private isStockError(error: any): boolean {
    if (!error) return false;

    // Verificar en el mensaje de error
    const errorMessage = error.message || error.error?.message || '';
    if (typeof errorMessage === 'string' &&
      (errorMessage.toLowerCase().includes('stock') ||
        errorMessage.toLowerCase().includes('insufficient'))) {
      return true;
    }

    // Verificar en la descripción del error
    const errorDescription = error.error?.error?.message || '';
    if (typeof errorDescription === 'string' &&
      (errorDescription.toLowerCase().includes('stock') ||
        errorDescription.toLowerCase().includes('insufficient'))) {
      return true;
    }

    // Verificar en el código de error si existe
    const errorCode = error.error?.error?.code || error.code || '';
    if (typeof errorCode === 'string' &&
      (errorCode.toLowerCase().includes('stock') ||
        errorCode.toLowerCase().includes('insufficient'))) {
      return true;
    }

    return false;
  }

  loadPikUps() {
    const slug = this.selectedSommelierService.currentSlugValue;
    this.pickUpService.getPickUps(slug).pipe(
      switchMap(pickups => {
        this.pickUps = pickups;
        // Filtrar solo pickups que tienen ColppyWarehouseName configurado
        // y usar ColppyWarehouseName como valor (para Colppy) pero mostrar Name en la etiqueta
        this.pickUpsOptions = pickups
          .filter(p => p.isVisible !== false)
          .filter(p => p.colppyWarehouseName && p.colppyWarehouseName.trim() !== '')
          .map(c => ({
            value: c.colppyWarehouseName!, // Usar ColppyWarehouseName como valor para Colppy
            label: c.name + " - " + c.address // Mostrar Name en la etiqueta para el usuario
          }));

        // Verificar si el usuario es cliente para seleccionar automáticamente el primer pickup
        return this.config.getOne$('currentUser').pipe(take(1));
      })
    ).subscribe(user => {
      const roles = (user as any)?.roles ?? [];
      // Si el usuario es cliente y hay pickups disponibles, seleccionar automáticamente el primero
      // PERO solo si el método de entrega es Pickup
      if (roles.includes('client') && this.pickUpsOptions.length > 0) {
        const firstPickupValue = this.pickUpsOptions[0].value;
        // Solo establecer el valor si el método de entrega es Pickup
        if (this.form.get('deliveryMethod')?.value === DeliveryMethod.Pickup) {
          this.form.get('shippingAddressLine')?.setValue(firstPickupValue);
        }
        // Guardar el valor para que se mantenga al cambiar entre métodos de entrega
        this.savedPickupValue = firstPickupValue;
      }
    });
  }

  // winepassport-api#60 — Allaria+ (Web-Checkout). No tokeniza tarjeta acá: crea la orden
  // (Pending) en el backend, guarda el clientPurchaseId pendiente para cuando el pagador vuelva
  // (Allaria no permite pasarlo en la URL de retorno, que es fija) y manda al navegador al
  // formulario de pago de Allaria+.
  private async payWithAllaria(orderDto: CreateOrderWithItemsDto) {
    this.isLoading = true;
    try {
      const result = await this.mercadoPagoOrderService.startAllariaCheckout(orderDto).toPromise();
      const clientPurchaseId = result?.clientPurchaseId;
      const redirectUrl = result?.redirectUrl;
      if (!clientPurchaseId || !redirectUrl || !this.isAllariaUrl(redirectUrl)) {
        throw new Error('Respuesta incompleta o inesperada de startAllariaCheckout');
      }
      localStorage.setItem('allariaPendingClientPurchaseId', clientPurchaseId);
      window.location.href = redirectUrl;
    } catch (err) {
      this.isLoading = false;
      if (this.isStockError(err)) this.notificationService.warning('::InsufficientStockWarning', '::StockWarning');
      else this.notificationService.error('', 'Error al iniciar el pago con Allaria+');
    }
  }

  // Solo se redirige al pagador a un https del dominio de Allaria+ (dev o prod).
  private isAllariaUrl(value: string): boolean {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && /(^|\.)allariamas\.(dev|com\.ar)$/.test(url.hostname);
    } catch {
      return false;
    }
  }

  private async handleTransfer(orderDto: CreateOrderWithItemsDto) {
    this.isLoading = true;
    try {
      const response = await this.ordersService.createWithItems(orderDto).toPromise();

      if (response.hasStockIssues) {
        const cancelled = await this.handleStockIssues(response.stockIssues, response.noStockInAnyDeposit);
        if (cancelled) return;

        // El usuario quiere continuar: reenviar con forceCreate: true
        const forceOrderDto: CreateOrderWithItemsDto = { ...orderDto, forceCreate: true };
        const forceResponse = await this.ordersService.createWithItems(forceOrderDto).toPromise();
        this.cartStateService.clear();
        sessionStorage.removeItem('appliedCoupon');
        const formValue = this.form.value;
        const alias = formValue.bankAlias?.replace('Alias - ', '') || 'WINE.P';
        const cbu = formValue.bankCbu?.replace('CBU - ', '') || '0170093020000033322542';
        const account = formValue.bankHolder?.replace('Titular - ', '') || 'GLOBEXA S.A.';
        this.selectedSommelierService.navigate(['payment-status'], {
          queryParams: { status: 'transfer', orderId: forceResponse.order.id, account, alias, cbu }
        });

      } else {
        this.cartStateService.clear();
        sessionStorage.removeItem('appliedCoupon');
        const formValue = this.form.value;
        // Extraer los valores reales del formulario, removiendo los prefijos
        const alias = formValue.bankAlias?.replace('Alias - ', '') || 'WINE.P';
        const cbu = formValue.bankCbu?.replace('CBU - ', '') || '0170093020000033322542';
        const account = formValue.bankHolder?.replace('Titular - ', '') || 'GLOBEXA S.A.';

        this.selectedSommelierService.navigate(['payment-status'], {
          queryParams: {
            status: 'transfer',
            orderId: response.order.id,
            account: account,
            alias: alias,
            cbu: cbu
          }
        });
      }
    } catch (err) {
      if (this.isStockError(err)) this.notificationService.warning('::InsufficientStockWarning', '::StockWarning');

      else this.notificationService.error('', 'Error al crear la orden');

      this.destroyCardForm();
      setTimeout(() => this.initCardForm(), 100);

    } finally { this.isLoading = false; }
  }


  private async handleStockIssues(stockIssues: any[], noStockInAnyDeposit?: boolean): Promise<boolean> {
    if (!stockIssues?.length) return false;

    const winesList = stockIssues
      .map(i => {
        const availableQty = Math.floor(i.availableQuantity);
        if (i.status === StockStatus.Insufficient) {
          return `• ${i.wineName} (Stock disponible: ${availableQty} unidades)`;
        }
        return `• ${i.wineName}`;
      })
      .join('<br>');

    const deliveryMethod = this.form.get('deliveryMethod')?.value;
    const isPickup = deliveryMethod === DeliveryMethod.Pickup;
    // Si es pickup pero no hay stock en NINGUN punto de retiro, tratar igual que MeetWithSommelier (sin opción de continuar)
    const canForce = isPickup && !noStockInAnyDeposit;

    if (canForce) {
      const body =
        `${this.localization.instant('::StockModalUnavailableList')}<br>${winesList}<br><br>` +
        `${this.localization.instant('::StockModalInstructions')}`;

      const confirmed = await this.confService.confirm({
        title: this.localization.instant('::StockWarningTitle'),
        message: body,
        type: 'warning',
        showCancelButton: true,
        confirmText: this.localization.instant('::StockModalContinue'),
        cancelText: this.localization.instant('::StockModalBackToCart')
      }).toPromise();

      if (!confirmed) {
        this.selectedSommelierService.navigate(['shopping-cart']);
        return true; // cancelado
      }
      return false; // el usuario quiere continuar con forceCreate
    } else {
      // No hay stock en ningún punto (o método != pickup): no permitimos continuar
      const body =
        `${this.localization.instant('::StockWarningMessageAllDeposits')}<br>${winesList}<br><br>` +
        `${this.localization.instant('::StockWarningInstructionsAllDeposits')}`;

      const wantsToCancel = await this.confService.confirm({
        title: this.localization.instant('::StockWarningTitleAllDeposits'),
        message: body,
        type: 'warning',
        showCancelButton: true,
        confirmText: this.localization.instant('::StockModalCancel'),
        cancelText: this.localization.instant('::StockModalBackToCart')
      }).toPromise();

      if (!wantsToCancel) {
        // Hizo click en "Volver al carrito"
        this.selectedSommelierService.navigate(['shopping-cart']);
      }
      return true; // Siempre cancela la creación automática para otros métodos
    }
  }

  private async loadProfile() {
    this.isLoadingProfile = true;
    try {
      const profile = await firstValueFrom(this.profileService.getProfile());
      this.userProfile = profile;
      this.initialPhoneNumber = profile.phoneNumber;
      this.initialDocumentNumber = profile.documentNumber;
      this.initialAddress = profile.address;
      this.initialProvince = profile.province;

      this.isProfileIncomplete = !this.initialPhoneNumber || !this.initialDocumentNumber || !this.initialAddress || !this.initialProvince;

      this.form.patchValue({
        phoneNumber: this.initialPhoneNumber,
        dni: this.initialDocumentNumber,
        profileAddress: this.initialAddress,
        profileProvince: this.initialProvince
      });

      this.updateValidators();
    } catch (error) {
      console.error('Error cargando perfil:', error);
    } finally {
      this.isLoadingProfile = false;
    }
  }

  private async syncProfileIfNeeded() {
    const { phoneNumber, dni, profileAddress, profileProvince } = this.form.value;

    if (phoneNumber !== this.initialPhoneNumber || dni !== this.initialDocumentNumber
      || profileAddress !== this.initialAddress || profileProvince !== this.initialProvince) {
      const updatedProfile: CustomUpdateProfileDto = {
        ...this.userProfile,
        phoneNumber: phoneNumber,
        documentNumber: dni,
        address: profileAddress,
        province: profileProvince
      };

      try {
        await firstValueFrom(this.profileService.updateProfile(updatedProfile));
        this.initialPhoneNumber = phoneNumber;
        this.initialDocumentNumber = dni;
        this.initialAddress = profileAddress;
        this.initialProvince = profileProvince;
        this.isProfileIncomplete = false;
      } catch (error) {
        this.notificationService.error('::ProfileUpdateError', 'Error');
        throw error;
      }
    }
  }

  /**
   * Detecta la marca de la tarjeta a partir de su BIN (primeros dígitos), vía
   * los rangos publicados por cada marca. Devuelve uno de los valores que
   * reconoce el backend en PaywayPaymentProvider.MapPaymentMethodId
   * ('visa' | 'mastercard' | 'amex' | 'naranja' | 'cabal' | 'maestro'),
   * o 'visa' como default si no matchea ningún rango conocido.
   */
  detectCardBrand(cardNumber: string): string {
    const digits = (cardNumber || '').replace(/\D/g, '');
    if (!digits) return 'visa';

    const bin6 = digits.substring(0, 6);
    const bin4 = digits.substring(0, 4);
    const bin2 = digits.substring(0, 2);
    const binNum6 = parseInt(bin6, 10);
    const binNum4 = parseInt(bin4, 10);

    // Amex: 34, 37
    if (bin2 === '34' || bin2 === '37') return 'amex';

    // Naranja (Argentina): BIN 589562
    if (bin6 === '589562') return 'naranja';

    // Cabal (Argentina): rangos publicados de BIN
    if (
      bin6 === '589657' ||
      (binNum6 >= 603522 && binNum6 <= 603523) ||
      (binNum6 >= 604201 && binNum6 <= 604250) ||
      (binNum6 >= 604400 && binNum6 <= 604460) ||
      (binNum6 >= 605701 && binNum6 <= 605750) ||
      (binNum6 >= 627170 && binNum6 <= 627179) ||
      bin6 === '636853'
    ) {
      return 'cabal';
    }

    // Mastercard: 51-55, o rango 2221-2720
    if (
      (binNum4 >= 5100 && binNum4 <= 5599) ||
      (binNum4 >= 2221 && binNum4 <= 2720)
    ) {
      return 'mastercard';
    }

    // Maestro: 50, 56-58, 6304, 6759, 6761-6763
    if (
      bin2 === '50' ||
      (binNum4 >= 5600 && binNum4 <= 5899) ||
      bin4 === '6304' || bin4 === '6759' ||
      (binNum4 >= 6761 && binNum4 <= 6763)
    ) {
      return 'maestro';
    }

    // Visa: empieza con 4
    if (digits.startsWith('4')) return 'visa';

    return 'visa'; // default si no matchea ninguna marca conocida
  }

  /** Actualiza la marca y el BIN detectados mientras el usuario tipea el número de tarjeta, y recalcula cuotas si cambió alguno (las cuotas dependen del BIN, no solo de la marca, para saber si es débito) */
  onPaywayCardNumberInput(event: any): void {
    const cardNumber = event?.target?.value || '';
    const digits = cardNumber.replace(/\D/g, '');
    const detectedBrand = this.detectCardBrand(cardNumber);
    const detectedBin = digits.length >= 6 ? digits.substring(0, 6) : '';
    if (detectedBrand !== this.paywayCardBrand || detectedBin !== this.paywayCardBin) {
      this.paywayCardBrand = detectedBrand;
      this.paywayCardBin = detectedBin;
      this.loadInstallments();
    }
  }

  onExpirationInput(event: any) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Remover todo lo que no sea dígito
    
    if (value.length > 4) {
      value = value.substring(0, 4);
    }
    
    if (value.length > 2) {
      input.value = value.substring(0, 2) + '/' + value.substring(2);
    } else {
      input.value = value;
    }
  }

}
